<template>
  <div class="flex flex-col gap-2">
    <!-- Breadcrumb + Collapse Icon -->
    <div class="flex items-center gap-2">
      <VaIconMenuCollapsed
        class="cursor-pointer"
        :class="{ 'x-flip': !isSidebarMinimized }"
        :color="collapseIconColor"
        @click="isSidebarMinimized = !isSidebarMinimized"
      />

      <nav class="flex items-center">
        <VaBreadcrumbs>
          <VaBreadcrumbsItem :label="homeLabel" :to="homeRoute" />
          <VaBreadcrumbsItem
            v-for="item in items"
            :key="item.label"
            :label="item.label"
            @click="handleBreadcrumbClick(item)"
          />
        </VaBreadcrumbs>
      </nav>
    </div>

    <!-- Browsing History -->
    <div>
      <ElScrollbar class="browsing-history-scrollbar">
        <div class="flex gap-2 flex-wrap scrollbar-flex-content">
          <button
            v-for="item in browsingHistory"
            :key="item.to"
            class="scrollbar-demo-item text-xs text-blue-600 hover:underline bg-gray-100 rounded px-2 py-1"
            @click="router.push(item.to)"
          >
            {{ item.label }}
          </button>
        </div>
      </ElScrollbar>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import VaIconMenuCollapsed from '../icons/VaIconMenuCollapsed.vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import NavigationRoutes from '../sidebar/NavigationRoutes'
import { ElScrollbar } from 'element-plus'
import 'element-plus/es/components/scrollbar/style/css'
import { getRoleLandingPage } from '../../utils/userRoleUtils'

const { isSidebarMinimized } = storeToRefs(useGlobalStore())
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
}

const homeRoute = computed(() => {
  const landingPage = getRoleLandingPage()
  return { path: landingPage }
})

const homeLabel = computed(() => {
  return 'Home'
})

const findRouteName = (name: string) => {
  const traverse = (routers: any[]): string => {
    for (const router of routers) {
      if (router.name === name) {
        return router.displayName
      }
      if (router.children) {
        const result = traverse(router.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }

  return traverse(NavigationRoutes.routes)
}

const items = computed(() => {
  const result: BreadcrumbNavigationItem[] = []
  route.matched.forEach((route) => {
    const labelKey = findRouteName(route.name as string)
    if (!labelKey) return
    result.push({
      label: t(labelKey),
      to: route.path,
      hasChildren: route.children?.length > 0 || false,
    })
  })
  return result
})

const browsingHistory = ref<BreadcrumbNavigationItem[]>([])

watch(
  () => route.fullPath,
  () => {
    const labelKey = findRouteName(route.name as string)
    if (!labelKey) return

    const alreadyExists = browsingHistory.value.find((item) => item.to === route.fullPath)
    if (!alreadyExists) {
      browsingHistory.value.push({
        label: t(labelKey),
        to: route.fullPath,
        hasChildren: route.children?.length > 0 || false,
      })

      if (browsingHistory.value.length > 100) {
        browsingHistory.value.shift()
      }
    }
  },
  { immediate: true },
)

const { getColor } = useColors()
const collapseIconColor = computed(() => getColor('secondary'))

const handleBreadcrumbClick = (item: BreadcrumbNavigationItem) => {
  if (!item.hasChildren) {
    router.push(item.to)
  }
}
</script>

<style scoped lang="scss">
.x-flip {
  transform: scaleX(-100%);
}

.browsing-history-scrollbar {
  max-height: 100px;
  padding: 0.5rem;
}
.scrollbar-flex-content {
  display: flex;
  width: fit-content;
}
.scrollbar-demo-item {
  display: inline-block;
  position: relative;
  cursor: pointer;
  height: 26px;
  line-height: 26px;
  border: 1px solid #d8dce5;
  color: #495060;
  background: #fff;
  padding: 0 8px;
  font-size: 12px;
  margin-left: 5px;
  margin-top: 4px;
  text-align: center;
  border-radius: 0px;
  background: var(--el-color-success);
  color: var(--el-color-white);
  display: inline-block;
  position: relative;
  cursor: pointer;
  height: 26px;
  line-height: 26px;
  border: 1px solid #d8dce5;
  color: #495060;
  background: #fff;
  padding: 0 8px;
  font-size: 12px;
  margin-left: 5px;
  margin-top: 4px;
}
</style>
